import { useState, useEffect, useCallback, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'native' | 'ios' | 'macos-safari' | null;

const DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-installed';
const MANUAL_DELAY_MS = 30_000;

function detectIos(): boolean {
  return /iP(hone|od|ad)/.test(navigator.userAgent) && !(window as any).MSStream;
}

function detectMacSafari(): boolean {
  const ua = navigator.userAgent;
  return /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

function wasDismissed(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === 'true';
}

function detectChromium(): boolean {
  const ua = navigator.userAgent;
  return (/Chrome/.test(ua) || /Edg/.test(ua)) && !/OPR/.test(ua);
}

function detectPlatform(): InstallPlatform {
  if (detectIos()) return 'ios';
  if (detectMacSafari()) return 'macos-safari';
  if (detectChromium()) return 'native';
  return null;
}

export function useInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>(detectPlatform);
  const [isInstalled, setIsInstalled] = useState(
    () => isInStandaloneMode() || localStorage.getItem(INSTALLED_KEY) === 'true'
  );
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  const isIosDevice = platform === 'ios';

  // Passive prompt (auto-shows after delay or on beforeinstallprompt)
  useEffect(() => {
    if (isInstalled || wasDismissed()) return;

    if (platform === 'ios' || platform === 'macos-safari') {
      const timer = setTimeout(() => setShowPrompt(true), MANUAL_DELAY_MS);
      return () => clearTimeout(timer);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setPlatform('native');
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isInstalled, platform]);

  // Detect when app gets installed
  useEffect(() => {
    const handler = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.setItem(INSTALLED_KEY, 'true');
    };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt.current) return false;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      setShowPrompt(false);
      setIsInstalled(true);
      localStorage.setItem(INSTALLED_KEY, 'true');
      deferredPrompt.current = null;
      return true;
    }
    deferredPrompt.current = null;
    return false;
  }, []);

  const dismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem(DISMISSED_KEY, 'true');
    deferredPrompt.current = null;
  }, []);

  const canInstall = !isInstalled && (platform === 'native' || platform === 'ios' || platform === 'macos-safari');

  return { showPrompt, isIosDevice, platform, isInstalled, canInstall, install, dismiss };
}
